import moment from 'moment-timezone';

export interface TimezoneInfo<T extends string | undefined = string> {
  shortTZ: T;
  fullTZ: T;
}

export const isSameDay = (day1: Date | undefined, day2: Date | undefined): boolean => {
  if (!day1 || !day2) return false;
  return (
    day1.getDate() === day2.getDate() &&
    day1.getMonth() === day2.getMonth() &&
    day1.getFullYear() === day2.getFullYear()
  );
};

export const parseISOString = (ISODate: string): Date => {
  return new Date(ISODate.replace(/\s+/g, ''));
};

/** 120 days, expressed in milliseconds. */
const TRACKING_TOO_EARLY_WINDOW_MS = 120 * 24 * 60 * 60 * 1000;

/**
 * Returns true when `estimatedDelivery` is still ahead of us and no more than 120 days out.
 *
 * The comparison is deliberately directional. A package whose estimated delivery has already
 * passed is not "too early" to track, so a past date must return false and let the caller fall
 * through to the stale-tracking-data state instead. An earlier version compared the absolute
 * difference, which made a date 12 days in the past indistinguishable from one 12 days in the
 * future and caused overdue packages with no tracking events to be shown a "tracking too soon"
 * message. See https://t.corp.amazon.com/V2383383464.
 */
export const isUpcomingWithin120Days = (estimatedDelivery: Date): boolean => {
  const millisecondsUntilDelivery = estimatedDelivery.getTime() - Date.now();
  return millisecondsUntilDelivery >= 0 && millisecondsUntilDelivery <= TRACKING_TOO_EARLY_WINDOW_MS;
};

export const formatEventTime = (dateString: string): TimezoneInfo => {
  const date: Date = new Date(dateString);
  const guessedZone: string = moment.tz?.guess();
  const zonedDate = moment(date).tz(guessedZone);

  // Get short timezone name
  const shortTZ: string = zonedDate.format('z');

  // Get full timezone name
  const fullTZ: string =
    new Intl.DateTimeFormat(moment.locale(), {
      timeZone: guessedZone,
      timeZoneName: 'long',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return { shortTZ, fullTZ };
};
